// ─── Événements du calendrier (données PARTAGÉES avec MoPCal) ─────
// Porté de MoPCal (services/evenements.js), version essentielle. Structure
// Firestore INCHANGÉE → My Calendar et MoPCal lisent/écrivent les MÊMES docs :
//   evenements/{calId}/jours/{YYYY-MM-DD} → { items: [{id, titre, heure, note,
//                                              creePar, creeParNom, creeLe}] }
// `calId` = calendrier hérité de l'espace (useEspace().tenantRangement).
// (Récurrences, historique, visibilité fine, plages multi-jours : incréments suivants.)
import { db, journaliser } from '@mop/core';
import {
  collection, doc, onSnapshot, setDoc, getDoc, addDoc, updateDoc, deleteDoc,
  arrayUnion, deleteField, query, where, documentId,
} from 'firebase/firestore';

export const dateKey = (annee, mois0, jour) =>
  `${annee}-${String(mois0 + 1).padStart(2, '0')}-${String(jour).padStart(2, '0')}`;

const sansUndef = (obj) => {
  const out = {};
  for (const k of Object.keys(obj || {})) if (obj[k] !== undefined) out[k] = obj[k];
  return out;
};

// Écoute les événements d'un calendrier (map { 'YYYY-MM-DD': [items] }).
// `opts.min`/`opts.max` : bornage à la fenêtre visible (sur l'id du doc).
// Champs « a la carte » d'un EVENEMENT — meme mecanique que les contacts
// (cf. ChampsPerso) : ajoutes d'un clic, retires s'ils restent vides.
// `saisie` = type de widget/clavier (cf. SAISIES_CHAMP du service contacts).
export const CHAMPS_EVENEMENT = [
  { cle: 'lienVisio',   nom: 'Lien visio',     emoji: '\u{1F4BB}', saisie: 'lien' },
  { cle: 'reservation', nom: 'Reservation',    emoji: '\u{1F3AB}', saisie: 'texte' },
  { cle: 'contactEvt',  nom: 'Contact',        emoji: '\u{1F4DE}', saisie: 'tel' },
  { cle: 'budget',      nom: 'Budget',         emoji: '\u{1F4B6}', saisie: 'nombre' },
  { cle: 'aApporter',   nom: 'A apporter',     emoji: '\u{1F9F3}', saisie: 'texte' },
  { cle: 'tenue',       nom: 'Tenue',          emoji: '\u{1F454}', saisie: 'texte' },
  { cle: 'echeance',    nom: 'Date limite',    emoji: '\u23F3', saisie: 'date' },
];

// Ne garde que les champs qui portent une valeur : un champ ajoute puis laisse
// vide ne doit pas encombrer l'evenement (meme regle que les contacts).
export function nettoyerChampsEvt(champs) {
  return (Array.isArray(champs) ? champs : [])
    .map(c => ({ cle: c.cle, nom: (c.nom || '').trim(), emoji: c.emoji || '',
                 saisie: c.saisie || 'texte', valeur: (c.valeur == null ? '' : String(c.valeur)).trim() }))
    .filter(c => c.cle && c.valeur);
}

// `onErreur(message)` remonté — même correction que sur les contacts/le
// journal/la messagerie : un refus de règle donnait un calendrier VIDE en
// silence, indiscernable d'un mois qui n'a réellement aucun événement.
export function ecouterEvenements(calId, callback, opts, onErreur) {
  if (!calId) { callback({}); return () => {}; }
  const ref = collection(db, 'evenements', calId, 'jours');
  const q = (opts && opts.min && opts.max)
    ? query(ref, where(documentId(), '>=', opts.min), where(documentId(), '<=', opts.max))
    : ref;
  return onSnapshot(q, (snap) => {
    const map = {};
    snap.forEach(d => { map[d.id] = d.data().items || []; });
    callback(map);
  }, (e) => { callback({}); onErreur?.(e?.message || String(e)); });
}

// `options.sansJournal` : pour un déplacement (suppression+ajout regroupées en
// une seule entrée « modification », cf. `deplacerEvenement` plus bas — même
// patron que MoPCal, qui journalisait sinon DEUX fois un seul geste).
export async function ajouterEvenement(calId, dk, evenement, options = {}) {
  const item = sansUndef({
    ...evenement,
    // Nettoyage a la FRONTIERE : quel que soit l'appelant, un champ « a la
    // carte » laisse vide n'est pas ecrit.
    ...(evenement.champs !== undefined ? { champs: nettoyerChampsEvt(evenement.champs) } : {}),
    id: evenement.id || `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    creeLe: evenement.creeLe || new Date().toISOString(),
  });
  await setDoc(doc(db, 'evenements', calId, 'jours', dk), { items: arrayUnion(item) }, { merge: true });
  if (!options.sansJournal) journaliser(calId, {
    type: 'evenement', action: 'creation',
    titre: evenement.titre || '', dateConcernee: dk,
    par: evenement.creePar || null, parNom: evenement.creeParNom || '',
  });
  return item.id;
}

// `auteur` optionnel = { uid, nom } — sans lui (import ICS en lot, migrations)
// la journalisation reste silencieuse plutôt que de noyer l'historique.
export async function modifierEvenement(calId, dk, eventId, modifications, auteur) {
  const ref = doc(db, 'evenements', calId, 'jours', dk);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  let titreEv = '';
  const items = (snap.data().items || []).map(e => {
    if (e.id !== eventId) return e;
    titreEv = e.titre || '';
    return { ...e, ...sansUndef(modifications),
      ...(modifications.champs !== undefined ? { champs: nettoyerChampsEvt(modifications.champs) } : {}),
      modifieLe: new Date().toISOString() };
  });
  await setDoc(ref, { items });
  if (auteur) journaliser(calId, {
    type: 'evenement', action: 'modification',
    titre: modifications?.titre || titreEv, dateConcernee: dk,
    par: auteur.uid || null, parNom: auteur.nom || '',
  });
}

export async function supprimerEvenement(calId, dk, eventId, auteur, options = {}) {
  const ref = doc(db, 'evenements', calId, 'jours', dk);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const tous = snap.data().items || [];
  const retire = tous.find(e => e.id === eventId);
  const items = tous.filter(e => e.id !== eventId);
  await setDoc(ref, { items });
  if (auteur && !options.sansJournal) journaliser(calId, {
    type: 'evenement', action: 'suppression',
    titre: retire?.titre || '', dateConcernee: dk,
    par: auteur.uid || null, parNom: auteur.nom || '',
  });
}

// ─── Événements RÉCURRENTS (données partagées MoPCal) ─────────────
// evenementsRecurrents/{calId}/events/{id} → { titre, heure, note, recurrence:
// Jours de la semaine (ordre d'affichage Lun→Dim, i = getDay()) — partagé par
// le formulaire d'événement et la fiche (règle de récurrence).
export const JOURS_SEM = [{ i: 1, l: 'L' }, { i: 2, l: 'M' }, { i: 3, l: 'M' }, { i: 4, l: 'J' }, { i: 5, l: 'V' }, { i: 6, l: 'S' }, { i: 0, l: 'D' }];

//   { type:'quotidien'|'hebdo'|'bimensuel'|'mensuel', joursSemaine?, dateDebut,
//     dateFin? }, exceptions:['YYYY-MM-DD'], actif? }
export function ecouterEvenementsRecurrents(calId, callback, onErreur) {
  if (!calId) { callback([]); return () => {}; }
  return onSnapshot(collection(db, 'evenementsRecurrents', calId, 'events'),
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    (e) => { callback([]); onErreur?.(e?.message || String(e)); });
}
export async function ajouterEvenementRecurrent(calId, evenement) {
  const ref = await addDoc(collection(db, 'evenementsRecurrents', calId, 'events'), sansUndef(evenement));
  journaliser(calId, {
    type: 'recurrence', action: 'creation', titre: evenement.titre || '',
    par: evenement.creePar || null, parNom: evenement.creeParNom || '',
  });
  return ref.id;
}
// `auteur` optionnel = { uid, nom, titre } — le titre de la série n'est pas
// lisible depuis le seul id, l'appelant le fournit pour l'entrée d'historique.
export async function supprimerEvenementRecurrent(calId, recId, auteur) {
  await deleteDoc(doc(db, 'evenementsRecurrents', calId, 'events', recId));
  journaliser(calId, {
    type: 'recurrence', action: 'suppression', titre: auteur?.titre || '',
    par: auteur?.uid || null, parNom: auteur?.nom || '',
  });
}
export async function modifierEvenementRecurrent(calId, recId, champs, auteur) {
  await updateDoc(doc(db, 'evenementsRecurrents', calId, 'events', recId), sansUndef(champs));
  // `detail` qualifie la modification (activation / désactivation / exceptions
  // touchées) pour un libellé d'historique plus parlant, même patron MoPCal.
  const detail = champs?.actif === true ? 'activation'
    : champs?.actif === false ? 'desactivation'
    : (champs && 'exceptions' in champs) ? 'exceptions' : null;
  journaliser(calId, {
    type: 'recurrence', action: 'modification', detail,
    titre: auteur?.titre || champs?.titre || '',
    par: auteur?.uid || null, parNom: auteur?.nom || '',
  });
}
// Lecture ponctuelle d'une série (exceptions / déplacements à l'enregistrement).
export async function lireEvenementRecurrent(calId, recId) {
  const snap = await getDoc(doc(db, 'evenementsRecurrents', calId, 'events', recId));
  return snap.exists() ? { id: recId, ...snap.data() } : null;
}
export async function retirerExceptionRecurrent(calId, recId, dk) {
  const ref = doc(db, 'evenementsRecurrents', calId, 'events', recId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  // Occurrence DÉPLACÉE : rétablir = supprimer la copie liée (elle revient à
  // sa date d'origine via la série), puis effacer la trace du déplacement.
  const dep = (snap.data().deplacements || {})[dk];
  if (dep?.evtId) { try { await supprimerEvenement(calId, dep.dk, dep.evtId); } catch (_) {} }
  await updateDoc(ref, {
    exceptions: (snap.data().exceptions || []).filter(x => x !== dk),
    [`deplacements.${dk}`]: deleteField(),
  });
}

// Déplace une OCCURRENCE de série : exception sur le jour d'origine + copie
// LIÉE (recIdOrigine/deplaceDe, type 'recurrent') + trace `deplacements`
// { [dkOrigine]: { dk, evtId } } sur le doc récurrent (champ ignoré par MoPCal).
export async function deplacerOccurrenceRecurrente(calId, recId, dkOrigine, dkCible, champsEvt) {
  const evtId = `dep_${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  await ajouterEvenement(calId, dkCible, { ...champsEvt, id: evtId, type: 'recurrent', recIdOrigine: recId, deplaceDe: dkOrigine });
  const ref = doc(db, 'evenementsRecurrents', calId, 'events', recId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const exceptions = snap.data().exceptions || [];
  await updateDoc(ref, {
    ...(exceptions.includes(dkOrigine) ? {} : { exceptions: [...exceptions, dkOrigine] }),
    [`deplacements.${dkOrigine}`]: { dk: dkCible, evtId },
  });
}
// La copie déplacée change encore de jour → maj de la trace.
export async function majDeplacementRecurrent(calId, recId, dkOrigine, dkCible, evtId) {
  await updateDoc(doc(db, 'evenementsRecurrents', calId, 'events', recId), { [`deplacements.${dkOrigine}`]: { dk: dkCible, evtId } });
}
// La copie déplacée est SUPPRIMÉE directement → l'exception d'origine reste,
// mais redevient une suppression simple (trace effacée).
export async function nettoyerDeplacementRecurrent(calId, recId, dkOrigine) {
  await updateDoc(doc(db, 'evenementsRecurrents', calId, 'events', recId), { [`deplacements.${dkOrigine}`]: deleteField() });
}
export async function ajouterExceptionRecurrent(calId, recId, dk) {
  const ref = doc(db, 'evenementsRecurrents', calId, 'events', recId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const exceptions = snap.data().exceptions || [];
  if (!exceptions.includes(dk)) await updateDoc(ref, { exceptions: [...exceptions, dk] });
}

// ─── Génération des occurrences + plages multi-jours (porté MoPCal) ──
const _parseDK = (dk) => { const [y, m, d] = dk.split('-').map(Number); return new Date(y, m - 1, d); };
const _lundiDe = (d) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const _fmtDK = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Occurrences des récurrences sur [debut, fin] → map { dk: [items] }.
export function genererOccurrences(recurrents, debut, fin) {
  const map = {};
  for (const rec of recurrents) {
    const { recurrence, exceptions = [], id, ...rest } = rec;
    if (rec.actif === false || !recurrence) continue;
    const { type, joursSemaine, dateDebut, dateFin, intervalle } = recurrence;
    const kInt = Math.max(1, Number(intervalle) || 1);   // « toutes les k semaines / tous les k mois »
    const dDebutRec = _parseDK(dateDebut);
    // PREMIÈRE OCCURRENCE réelle = premier jour coché >= date de début : c'est
    // ELLE qui ancre les modulos semaines/mois (ex. début samedi 1er, lundi
    // coché, toutes les 3 semaines → 1re occ. lundi 3, puis lundi 24 — pas une
    // ancre sur la semaine du 1er).
    const jsRec = joursSemaine && joursSemaine.length ? joursSemaine : null;
    const pOcc = (() => {
      if (!jsRec || type === 'quotidien') return dDebutRec;
      const d = new Date(dDebutRec.getFullYear(), dDebutRec.getMonth(), dDebutRec.getDate());
      for (let i = 0; i < 7; i++) { if (jsRec.includes(d.getDay())) return d; d.setDate(d.getDate() + 1); }
      return dDebutRec;
    })();
    const dDebut = _parseDK(dateDebut);
    const dFin = dateFin ? _parseDK(dateFin) : fin;
    let curr = new Date(Math.max(dDebut.getTime(), debut.getTime()));
    const endIter = new Date(Math.min(dFin.getTime(), fin.getTime()));
    while (curr <= endIter) {
      const dk = _fmtDK(curr);
      if (!exceptions.includes(dk)) {
        let match = false;
        if (type === 'quotidien') {
          // intervalle k > 1 : un jour sur k depuis la date de début.
          const nJ = kInt > 1 ? Math.round((new Date(curr.getFullYear(), curr.getMonth(), curr.getDate()) - new Date(dDebut.getFullYear(), dDebut.getMonth(), dDebut.getDate())) / 86400000) : 0;
          match = nJ >= 0 && nJ % kInt === 0;
        }
        else if (type === 'hebdo') {
          const js = joursSemaine && joursSemaine.length ? joursSemaine : [dDebut.getDay()];
          // intervalle k > 1 : jours cochés, une semaine sur k (semaines au lundi,
          // ancrées sur la semaine de la PREMIÈRE occurrence).
          const nSem = kInt > 1 ? Math.round((_lundiDe(curr) - _lundiDe(pOcc)) / (7 * 86400000)) : 0;
          match = js.includes(curr.getDay()) && nSem >= 0 && nSem % kInt === 0;
        }
        else if (type === 'bimensuel') {
          if (joursSemaine && joursSemaine.length) {
            // Jours cochés, UNE SEMAINE SUR DEUX (semaines au lundi), ancrée sur
            // la semaine de la PREMIÈRE occurrence.
            const nSem = Math.round((_lundiDe(curr) - _lundiDe(pOcc)) / (7 * 86400000));
            match = nSem >= 0 && nSem % 2 === 0 && joursSemaine.includes(curr.getDay());
          } else {
            // Historique (docs MoPCal) : tous les 14 jours depuis la date de début.
            const n = Math.round((new Date(curr.getFullYear(), curr.getMonth(), curr.getDate()) - new Date(dDebut.getFullYear(), dDebut.getMonth(), dDebut.getDate())) / 86400000);
            match = n >= 0 && n % 14 === 0;
          }
        }
        else if (type === 'mensuel') {
          // Jours cochés : chaque mois, dans la semaine de MÊME RANG que la date
          // de début (1re, 2e… semaine du mois). Sans sélection : même quantième.
          // intervalle k > 1 : appliqué tous les k mois seulement.
          const nMois = (curr.getFullYear() * 12 + curr.getMonth()) - (pOcc.getFullYear() * 12 + pOcc.getMonth());
          if (nMois >= 0 && nMois % kInt === 0) {
            if (joursSemaine && joursSemaine.length) match = joursSemaine.includes(curr.getDay()) && Math.ceil(curr.getDate() / 7) === Math.ceil(pOcc.getDate() / 7);
            else match = curr.getDate() === dDebut.getDate();
          }
        }
        if (match) { (map[dk] = map[dk] || []).push({ ...rest, id: `rec_${id}_${dk}`, recId: id, isRecurrent: true, recDef: recurrence, recExceptions: exceptions }); }
      }
      curr.setDate(curr.getDate() + 1);
    }
  }
  return map;
}

export const estPlageMultiJour = (item, dk) => !!(item && item.dateFin && item.dateFin > (dk || item.plageDebut || ''));

// Étale les événements multi-jours (dateFin) sur chaque jour traversé.
export function etalerPlages(map) {
  const out = {};
  const push = (dk, item) => { (out[dk] = out[dk] || []).push(item); };
  for (const dk of Object.keys(map || {})) {
    for (const item of (map[dk] || [])) {
      if (!estPlageMultiJour(item, dk)) { push(dk, item); continue; }
      const dFin = _parseDK(item.dateFin);
      let cur = _parseDK(dk);
      while (cur <= dFin) {
        const cdk = _fmtDK(cur);
        push(cdk, { ...item, id: `plage_${item.id}_${cdk}`, heure: '', estPlage: true, plageId: item.id, plageSourceDk: dk,
          plageHeureDebut: item.heure || '', plageHeureFin: item.heureFin || '',   // heures d'origine (rendu grille horaire)
          plageRole: cdk === dk ? 'debut' : (cdk === item.dateFin ? 'fin' : 'milieu') });
        cur.setDate(cur.getDate() + 1);
      }
    }
  }
  return out;
}

// Fusionne événements simples + occurrences récurrentes, puis étale les plages.
export function fusionner(evenements, recurrents, debut, fin) {
  const base = {};
  for (const dk of Object.keys(evenements || {})) base[dk] = [...(evenements[dk] || [])];
  const occ = genererOccurrences(recurrents || [], debut, fin);
  for (const dk of Object.keys(occ)) base[dk] = [...(base[dk] || []), ...occ[dk]];
  return etalerPlages(base);
}
